import { Component } from '@theme/component';
import Splide from '@splidejs/splide';

/**
 * A custom element for carousels with external controls.
 * Provides arrow navigation and autoplay support.
 *
 * @typedef {object} ApcCarouselWithNavRefs
 * @property {HTMLButtonElement} [previous] - Previous arrow button.
 * @property {HTMLButtonElement} [next] - Next arrow button.
 *
 * @extends Component<ApcCarouselWithNavRefs>
 */
class ApcCarouselWithNav extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {import('@splidejs/splide').Splide} */
  #splide;

  connectedCallback() {
    super.connectedCallback();

    const hasAutoplay = this.hasAttribute('data-autoplay');
    const autoplayInterval = hasAutoplay
      ? parseInt(this.getAttribute('data-autoplay-interval') || '5', 10) * 1000
      : undefined;

    const carousel = this.querySelector('.splide');
    if (!carousel) return;

    this.#splide = new Splide(
      carousel,
      /** @type {import('@splidejs/splide').Options} */
      {
        autoWidth: false,
        arrows: false,
        padding: 0,
        gap: 0,
        perMove: 1,
        perPage: 1,
        omitEnd: false,
        pagination: false,
        mediaQuery: 'max',
        easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
        focus: 'center',
        type: 'fade',
        speed: 1000,
        rewind: true,
        autoplay: hasAutoplay,
        interval: autoplayInterval,
        pauseOnHover: hasAutoplay,
        pauseOnFocus: hasAutoplay,
      },
    );

    this.#splide.mount();
    this.#removeInvalidSlideRoles();

    this.#bindControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#splide?.destroy(true);
  }

  /**
   * Removes invalid ARIA roles that Splide sets on <li> slide elements.
   */
  #removeInvalidSlideRoles() {
    this.querySelectorAll('.splide__slide[role]').forEach((slide) => slide.removeAttribute('role'));
  }

  /**
   * Binds event listeners to external control elements.
   */
  #bindControls() {
    const { signal } = this.#abortController;

    if (this.refs.previous) {
      this.refs.previous.addEventListener('click', () => this.#splide.go('<'), { signal });
    }

    if (this.refs.next) {
      this.refs.next.addEventListener('click', () => this.#splide.go('>'), { signal });
    }
  }
}

if (!customElements.get('apc-carousel-with-nav')) {
  customElements.define('apc-carousel-with-nav', ApcCarouselWithNav);
}
